/** Thin fetch wrapper. One place that knows the base URL and error shape. */

import type {
  AlertRule, BacktestDetail, BacktestRun, CandleResponse, EconEvent, Health,
  Horizon, MtfConsensus, NewsItem, OverlayResponse, RiskConfig, RiskFlag, Signal,
  StructureResponse, Symbol, Watchlist,
  SourceHealthRow,
} from './types'

// Same-origin in dev (Vite proxies /api), overridable for a split deployment.
const BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch {
      /* keep the status text */
    }
    throw new ApiError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

function query(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) value.forEach((v) => search.append(key, String(v)))
    else search.append(key, String(value))
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

export const api = {
  health: () => request<Health>('/health'),

  symbols: () => request<Symbol[]>('/symbols'),

  candles: (ticker: string, timeframe: string, limit = 500) =>
    request<CandleResponse>(`/candles/${ticker}${query({ timeframe, limit })}`),

  overlays: (ticker: string, timeframe: string, names?: string[], limit = 500) =>
    request<OverlayResponse>(`/overlays/${ticker}${query({ timeframe, limit, names })}`),

  structure: (ticker: string, timeframe: string) =>
    request<StructureResponse>(`/structure/${ticker}${query({ timeframe })}`),

  signals: (params: {
    ticker?: string
    open_only?: boolean
    timeframe?: string[]
    horizon?: Horizon[]
    limit?: number
  } = {}) => request<Signal[]>(`/signals${query(params)}`),

  mtf: (ticker: string) => request<MtfConsensus[]>(`/mtf/${ticker}`),

  riskFlags: (ticker?: string) => request<RiskFlag[]>(`/risk-flags${query({ ticker })}`),

  news: (params: { ticker?: string; hours?: number; limit?: number } = {}) =>
    request<NewsItem[]>(`/news${query(params)}`),

  events: (params: { hours_ahead?: number; impact?: string[] } = {}) =>
    request<EconEvent[]>(`/events${query(params)}`),

  watchlists: () => request<Watchlist[]>('/watchlists'),

  addToWatchlist: (watchlistId: number, ticker: string) =>
    request<Watchlist>(`/watchlists/${watchlistId}/items/${ticker}`, { method: 'POST' }),

  removeFromWatchlist: (watchlistId: number, ticker: string) =>
    request<Watchlist>(`/watchlists/${watchlistId}/items/${ticker}`, { method: 'DELETE' }),

  mute: (ticker: string, minutes: number) =>
    request<{ ticker: string; muted_until: string }>(`/watchlists/mute/${ticker}`, {
      method: 'POST',
      body: JSON.stringify({ minutes }),
    }),

  unmute: (ticker: string) =>
    request<{ ticker: string; muted_until: null }>(`/watchlists/mute/${ticker}`, {
      method: 'DELETE',
    }),

  alertRules: () => request<AlertRule[]>('/config/alerts'),

  updateAlertRule: (id: number, patch: Partial<AlertRule>) =>
    request<AlertRule>(`/config/alerts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  riskConfig: () => request<RiskConfig>('/config/risk'),

  updateRiskConfig: (patch: Partial<RiskConfig>) =>
    request<RiskConfig>('/config/risk', { method: 'PATCH', body: JSON.stringify(patch) }),

  backtestRuns: (ticker?: string) => request<BacktestRun[]>(`/backtest/runs${query({ ticker })}`),

  backtestRun: (id: number) => request<BacktestDetail>(`/backtest/runs/${id}`),

  runBacktest: (body: { ticker: string; horizon: Horizon; cost_bps?: number }) =>
    request<BacktestDetail>('/backtest/run', { method: 'POST', body: JSON.stringify(body) }),

  /** Ingestion source freshness, for the Nguồn dữ liệu tab. */
  sourceHealth: () => request<{ sources: SourceHealthRow[]; stale: string[] }>('/health/sources'),
}
